import { useEffect, useRef, useState } from 'react';

/**
 * Limit value update to every {@link limitMs} milliseconds
 * NOTE: This is a replacement for react-use's useThrottle and useThrottleFn,
 * which don't currently work with React v18:
 * https://github.com/streamich/react-use/issues/2488
 * @param value The value to throttle
 * @param limitMs The limit in milliseconds
 * @returns T
 */
export const useThrottle = <T>(value: T, limitMs: number): T => {
	const [throttledValue, setThrottledValue] = useState<T>(value);
	const lastRan = useRef(Date.now());

	useEffect(() => {
		const handler = setTimeout(
			function () {
				if (Date.now() - lastRan.current >= limitMs) {
					setThrottledValue(value);
					lastRan.current = Date.now();
				}
			},
			limitMs - (Date.now() - lastRan.current),
		);

		return () => {
			clearTimeout(handler);
		};
	}, [value, limitMs]);

	return throttledValue;
};
